import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { useBilling } from "../lib/useBilling";
import { cn } from "../lib/utils";
import { Spinner } from "../components/ui";
import PerformanceReview from "../components/advisory/PerformanceReview";
import BoardMeeting from "../components/advisory/BoardMeeting";
import { Compass, MessagesSquare, History, Trash2, ChevronRight, AlertTriangle, Gavel, Lock } from "lucide-react";

function HistoryList({ onOpenReview, onOpenMeeting, refreshKey }) {
  const { t, lang } = useApp();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const [rev, meet] = await Promise.all([
      api.get("/advisory/reviews").then((r) => r.data).catch(() => []),
      api.get("/advisory/meetings").then((r) => r.data).catch(() => []),
    ]);
    const merged = [
      ...rev.map((r) => ({ ...r, _type: "review" })),
      ...meet.map((m) => ({ ...m, _type: "decision" })),
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setRows(merged);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (row, e) => {
    e.stopPropagation();
    await api.delete(`/advisory/${row._type === "review" ? "reviews" : "meetings"}/${row.id}`);
    load();
  };

  if (!rows) return <div className="py-16 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>;
  const filtered = rows.filter((r) => filter === "all" || (filter === "reviews" && r._type === "review") || (filter === "decisions" && r._type === "decision"));

  return (
    <div data-testid="adv-history-list">
      <div className="flex gap-1.5 mb-4">
        {["all", "reviews", "decisions"].map((fl) => (
          <button key={fl} onClick={() => setFilter(fl)} data-testid={`adv-log-filter-${fl}`}
            className={cn("text-xs px-3 py-1.5 rounded-lg border transition-colors", filter === fl ? "bg-text-primary text-background border-text-primary" : "hover:border-primary/40")}>
            {t(`log_filter_${fl}`)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? <p className="text-sm text-text-secondary py-10 text-center" data-testid="adv-history-empty">{t("adv_no_reviews")}</p> : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <button key={r.id} onClick={() => r._type === "review" ? onOpenReview(r.id) : onOpenMeeting(r.id)} data-testid={`adv-history-${r.id}`}
              className="w-full text-left bg-surface rounded-xl border p-4 hover:shadow-md transition-all flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1",
                    r._type === "review" ? "bg-primary/12 text-primary" : "bg-teal-500/12 text-teal-500")}>
                    {r._type === "review" ? <Compass className="w-3 h-3" /> : <Gavel className="w-3 h-3" />}
                    {t(`log_type_${r._type}`)}
                  </span>
                  <span className="text-xs font-semibold truncate">{r._type === "review" ? r.range_label : r.title}</span>
                  <span className="text-[10px] text-text-secondary">· {new Date(r.created_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</span>
                  {r.status === "generating" || ["summarizing", "questioning", "reviewing", "deliberating"].includes(r.status)
                    ? <span className="text-[10px] text-amber-500 inline-flex items-center gap-1"><Spinner className="w-3 h-3" />…</span> : null}
                </div>
                <p className="text-sm text-text-secondary mt-1 line-clamp-2 leading-snug">{r._type === "review" ? (r.overall_summary || "—") : r.question}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px]">
                  {r._type === "review" ? (
                    <>
                      {r.at_risk > 0 && <span className="text-red-500 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.at_risk} {t("adv_at_risk_count")}</span>}
                      {r.needs_attention > 0 && <span className="text-amber-500">{r.needs_attention} {t("adv_attention_count")}</span>}
                    </>
                  ) : (
                    r.recommendation && <span className="font-semibold text-text-primary">{t(`rec_${r.recommendation}`)} · {r.confidence}%</span>
                  )}
                  <span className="text-text-secondary">{r.action_count} {t("adv_actions").toLowerCase()}</span>
                </div>
              </div>
              <span onClick={(e) => remove(r, e)} data-testid={`adv-history-delete-${r.id}`}
                className="text-text-secondary hover:text-red-500 p-1.5 rounded hover:bg-surface-hover transition-colors"><Trash2 className="w-4 h-4" /></span>
              <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Advisory() {
  const { t, lang } = useApp();
  const { billing } = useBilling();
  const [tab, setTab] = useState("review");
  const [meta, setMeta] = useState(null);
  const [activeReview, setActiveReview] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/advisory/meta").then(({ data }) => setMeta(data)).catch(() => setMeta({ entities: [], spaces: [], boards: [], advisors: [] }));
  }, []);

  const openReview = async (id) => {
    const { data } = await api.get(`/advisory/reviews/${id}`);
    setActiveReview(data); setTab("review");
  };
  const openMeeting = async (id) => {
    const { data } = await api.get(`/advisory/meetings/${id}`);
    setActiveMeeting(data); setTab("board");
  };

  const TABS = [
    { key: "review", label: t("adv_tab_review"), icon: Compass },
    { key: "board", label: t("adv_tab_board"), icon: MessagesSquare },
    { key: "history", label: t("adv_tab_history"), icon: History },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="advisory-page">
      <div className="mb-6">
        <h1 className="font-heading font-black text-2xl flex items-center gap-2.5"><Compass className="w-6 h-6 text-primary" />{t("adv_title")}</h1>
        <p className="text-sm text-text-secondary mt-1">{t("adv_subtitle")}</p>
      </div>

      {billing && !billing.platform_admin && billing.limits?.advisory === 0 && (
        <div data-testid="advisory-locked-banner"
          className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-semibold text-text-primary">
              {lang === "fr" ? "Le Conseil IA est verrouillé sur votre plan Gratuit" : "AI Advisory is locked on your Free plan"}
            </div>
            <p className="text-sm text-text-secondary mt-0.5">
              {lang === "fr"
                ? "Passez au plan Pro pour les revues de performance, ou Business pour les board meetings interactifs."
                : "Upgrade to Pro for performance reviews, or Business for interactive board meetings."}
            </p>
          </div>
          <Link to="/settings" data-testid="advisory-upgrade-link"
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity">
            {lang === "fr" ? "Améliorer" : "Upgrade"}
          </Link>
        </div>
      )}

      <div className="flex gap-1 border-b mb-6">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`adv-tab-${tb.key}`}
              className={cn("px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2",
                tab === tb.key ? "border-primary text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary")}>
              <Icon className="w-4 h-4" />{tb.label}
            </button>
          );
        })}
      </div>

      {!meta ? (
        <div className="py-16 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : tab === "review" ? (
        <PerformanceReview meta={meta} activeReview={activeReview} setActiveReview={setActiveReview}
          onSaved={() => setRefreshKey((k) => k + 1)} />
      ) : tab === "board" ? (
        <BoardMeeting meta={meta} activeMeeting={activeMeeting} setActiveMeeting={setActiveMeeting}
          onSaved={() => setRefreshKey((k) => k + 1)} />
      ) : (
        <HistoryList onOpenReview={openReview} onOpenMeeting={openMeeting} refreshKey={refreshKey} />
      )}
    </div>
  );
}
